import {
  Box,
  Dialog,
  Divider,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  Theme,
} from '@material-ui/core'
import MuiLink from '@material-ui/core/Link'
import React, { useState, FC } from 'react'
import dayjs from 'dayjs'
import messages from '../messages'
import {
  Close as CloseIcon,
  ExpandLess as ExpandLessIcon,
} from '@material-ui/icons'
import CaretLeftIcon from '@icons/CaretLeftIcon'
import CopyIcon from '@src/icons/CopyIcon'
import { makeStyles } from '@material-ui/core/styles'
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent,
  Skeleton,
} from '@material-ui/lab'

import {
  getShipperObj,
  getTraceStatus,
} from '@src/utils/orderTrackingInfoMappings'
import { TrackingTracesDetail } from '@src/types/graphql-types'
import { useGetTrackingInfoQuery } from '@src/queries/GetTrackingInfoQuery.generated'
import { FormattedMessage } from 'react-intl'
import clsx from 'clsx'
import copy from 'copy-to-clipboard'
import ListError from '@src/components/ListError'

const useStyles = makeStyles((theme: Theme) => ({
  dialogTitle: {
    paddingBottom: theme.spacing(0),
  },
  invalidTimeline: {
    margin: 0,
  },
  timeline: {
    '& .MuiTimelineItem-missingOppositeContent:before': {
      padding: 0,
      flex: 0,
    },
    '& .MuiTimelineItem-root .MuiTimelineSeparator-root': {
      position: 'relative',
      top: 4,
    },
    '& .MuiTimelineDot-root': {
      margin: 0,
      background: theme.palette.secondary.main,
    },
    '& .MuiTimelineConnector-root': {
      backgroundColor: theme.palette.secondary.main,
    },
    '& .MuiTimelineContent-root': {
      padding: theme.spacing(0, 4, 4, 4),
    },
    '& .MuiDialogContent-root': {
      marginTop: theme.spacing(2),
      marginRight: theme.spacing(4),
    },
    '& .timelineDotWithIcon': {
      padding: 0,
      background: theme.palette.common.white,
      border: `2px solid ${theme.palette.secondary.main}`,
    },
    '& .timelineIcon': {
      fontSize: 10,
      color: theme.palette.secondary.main,
    },
  },
  copyIcon: {
    fontSize: '80%',
  },
  pointer: {
    cursor: 'pointer',
  },
  caretLeftIcon: {
    width: '10px',
    height: '10px',
  },
}))

const DATE_SCHEMA = 'YYYY年MM月DD日 HH:mm:ss'

interface OrderDetailTrackingInvalidProps {
  trackingUrl: string
  sendTime: string
  isTrackingFapiao: boolean
}

const OrderDetailTrackingInvalid: FC<OrderDetailTrackingInvalidProps> = ({
  trackingUrl,
  sendTime,
  isTrackingFapiao,
}) => {
  const classes = useStyles()
  const isSendTimeValid = dayjs(new Date(sendTime)).isValid()

  return (
    <Box my={8}>
      <Timeline className={clsx(classes.timeline, classes.invalidTimeline)}>
        <TimelineItem>
          <TimelineSeparator>
            <TimelineDot className={'timelineDotWithIcon'}>
              <ExpandLessIcon className={'timelineIcon'} />
            </TimelineDot>{' '}
          </TimelineSeparator>
          <TimelineContent>
            {isTrackingFapiao && isSendTimeValid ? (
              <span>
                发票已寄出，寄出时间:{' '}
                {dayjs(new Date(sendTime)).format('YYYY/MM/DD HH:mm:ss')}
              </span>
            ) : (
              <>
                <FormattedMessage
                  {...messages.ORDER_TRACKING_ORDER_STATUS_NO_RECORD}
                />
                <br />
                {trackingUrl.length > 0 && (
                  <>
                    <FormattedMessage
                      {...messages.ORDER_TRACKING_URL_MESSAGE}
                    />{' '}
                    <a href={trackingUrl} target="_blank" rel="noreferrer">
                      {trackingUrl}
                    </a>
                  </>
                )}
              </>
            )}
          </TimelineContent>
        </TimelineItem>
      </Timeline>
    </Box>
  )
}

const OrderDetailTrackingTimeline = ({
  traces,
}: {
  traces: Partial<TrackingTracesDetail>[]
}) => {
  const classes = useStyles()

  return (
    <>
      <Grid container spacing={0}>
        <Grid item>
          <Timeline className={classes.timeline}>
            {traces
              .sort((a, b) => {
                const dateA = a.acceptTime
                  ? new Date(a.acceptTime).getTime()
                  : new Date().getTime()
                const dateB = b.acceptTime
                  ? new Date(b.acceptTime).getTime()
                  : new Date().getTime()
                return dateB - dateA
              })
              .map((e, i) => (
                <TimelineItem key={i}>
                  <TimelineSeparator>
                    {i === 0 ? (
                      <TimelineDot className={'timelineDotWithIcon'}>
                        <ExpandLessIcon className={'timelineIcon'} />
                      </TimelineDot>
                    ) : (
                      <TimelineDot />
                    )}
                    {i !== traces.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent>
                    <Typography gutterBottom variant={'body2'}>
                      <Box
                        fontWeight={i === 0 ? 600 : 100}
                        lineHeight="1rem"
                        pb={1}
                        component={'span'}
                      >
                        {e.acceptStation}
                      </Box>
                    </Typography>
                    <Typography gutterBottom variant={'body2'}>
                      {e.acceptTime &&
                        dayjs(new Date(e.acceptTime)).format(DATE_SCHEMA)}
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
              ))}
          </Timeline>
        </Grid>
      </Grid>
    </>
  )
}

export const ContentCopyIcon = (props: { content: string }) => {
  const { content } = props
  const [copied, setCopied] = useState(false)
  const classes = useStyles()

  const copyTrackingNumber = () => {
    setCopied(true)
    copy(content)
    setTimeout(() => {
      setCopied(false)
    }, 3000)
  }

  return (
    <Box display="flex" alignItems="baseline">
      <Box marginX={1} className={classes.pointer}>
        <CopyIcon
          color="primary"
          className={classes.copyIcon}
          onClick={copyTrackingNumber}
        />
      </Box>
      {copied && (
        <Box className={classes.copyIcon}>
          <FormattedMessage {...messages.COPIED} />
        </Box>
      )}
    </Box>
  )
}

interface OrderDetailTrackingModalProps {
  open: boolean
  onClose: () => void
  trackingInfo: {
    trackingNumber: string
    trackingUrl: string
  } | null
  hideBackdrop?: boolean
  isTrackingFapiao?: boolean
  classes?: {}
  shipperCode?: string
  sendTime?: string
}

/**
 * This TrackingModel is used for displaying Chinese logistics info
 * @param props
 * @constructor
 */
export const OrderDetailTrackingModal: FC<OrderDetailTrackingModalProps> = (
  props
) => {
  const {
    trackingInfo,
    open,
    onClose,
    isTrackingFapiao = false,
    hideBackdrop = false,
    classes: passedClass = {},
    shipperCode = '',
    sendTime = '',
  } = props

  const trackingNumber = trackingInfo?.trackingNumber || ''
  const trackingUrl = trackingInfo?.trackingUrl || ''
  const classes = useStyles()

  const { data, loading, error } = useGetTrackingInfoQuery({
    variables: {
      trackingId: trackingNumber,
      shipperCode,
    },
    ssr: false,
    fetchPolicy: 'no-cache',
    skip: !open,
  })

  let traces: Partial<TrackingTracesDetail>[] = []
  if (data?.getTrackingInfo.trace) {
    traces = data.getTrackingInfo.trace
  }

  let { name, phone } = getShipperObj(
    data?.getTrackingInfo?.shipperCode?.toUpperCase()
  )
  //KDNiao doesn't support XIAOHU now
  if (shipperCode === 'XIAOHU') {
    name = '啸虎物流'
    phone = '021-32221555'
  }

  const renderCloseBtn = () => {
    if (isTrackingFapiao) {
      return (
        <MuiLink component="button" onClick={onClose} color="primary">
          <CaretLeftIcon className={classes.caretLeftIcon} />
          <Box component="span" fontSize={14} fontWeight={'fontWeightBold'}>
            返回发票详情
          </Box>
        </MuiLink>
      )
    }
    return (
      <IconButton onClick={onClose}>
        <CloseIcon />
      </IconButton>
    )
  }

  return (
    <Dialog
      open={open}
      classes={passedClass}
      hideBackdrop={hideBackdrop}
      onClose={onClose}
      aria-labelledby="orderDetailTrackingModal"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle
        id="orderDetailTrackingModal"
        className={classes.dialogTitle}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <FormattedMessage {...messages.ORDER_TRACKING_DETAILS} />
          {renderCloseBtn()}
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          /* Skeleton */
          <>
            <Box display="flex" justifyContent="space-between">
              <Skeleton animation="wave" width="10%" />
              <Skeleton animation="wave" width="10%" />
            </Box>
            <Box py={2}>
              <Skeleton animation="wave" width="20%" />
              <Skeleton animation="wave" width="20%" />
            </Box>
            <Divider />
            {Array(3)
              .fill(undefined)
              .map((_, index) => (
                <Box py={2} key={index}>
                  {[99, 70, 30].map((e, index2) => (
                    <Skeleton animation="wave" width={`${e}%`} key={index2} />
                  ))}
                </Box>
              ))}
          </>
        ) : error ? (
          <ListError />
        ) : (
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexDirection={name ? 'row' : 'row-reverse'}
            >
              {name && (
                <Typography gutterBottom variant={'subtitle2'}>
                  {name}
                </Typography>
              )}
              {data && (
                <Typography
                  gutterBottom
                  variant={'subtitle2'}
                  color={'secondary'}
                >
                  {getTraceStatus(data.getTrackingInfo.state)}
                </Typography>
              )}
            </Box>
            <Box display="flex">
              快递单号：{data?.getTrackingInfo?.logisticCode}
              <ContentCopyIcon
                content={data?.getTrackingInfo?.logisticCode || ''}
              />
            </Box>
            {phone && <Box>联系电话：{phone}</Box>}
            <Divider />
            {!error &&
            data?.getTrackingInfo?.success &&
            traces.length > 0 &&
            data?.getTrackingInfo?.state !== '0' ? (
              <OrderDetailTrackingTimeline traces={traces} />
            ) : (
              <OrderDetailTrackingInvalid
                trackingUrl={trackingUrl}
                isTrackingFapiao={isTrackingFapiao}
                sendTime={sendTime}
              />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
